/**
 * useRealtime — single React entry point for the realtime channel.
 * [milestone M5-T6]
 *
 * Mounts exactly once near the App root (inside `RequireAuth`, after the
 * boundary has confirmed there's a valid JWT). Opens the WebSocket, wires
 * its event callback into the projection dispatcher, and reopens on
 * JWT change. Returns nothing — pure side effect.
 *
 * React 18 StrictMode foot-gun:
 *   In dev, every effect runs twice. The first effect's cleanup will close
 *   the socket before the second effect opens a new one, so naively this
 *   works — but the "open / close / open" sequence flickers the
 *   reconnect counter at the server and pollutes logs. We add a small
 *   guard with a module-level dedupe key: if a connect is already in
 *   flight for the current (token, agentId) pair, the second effect is a
 *   no-op until the first one tears down.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { connectivity } from '@/lib/connectivity';
import { connect } from '@/lib/realtime/connection';
import {
  projectEvent,
  resetRealtimeFedCaches,
} from '@/lib/realtime/projection';
import { useAuthStore } from '@/store/auth';
import { useUIStore } from '@/store/ui';

/**
 * Mount once per authenticated session. The hook reads the JWT from the auth
 * store at mount time and on every change; switching agents (login → logout
 * → login as someone else) tears down the old socket and opens a fresh one.
 *
 * The hook itself returns nothing — components don't read realtime state
 * directly; they read the React Query cache that the projector writes into.
 *
 * Socket lifecycle → `connectivity.setWs(...)` (2026-09 early warning, spec
 * `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.6 item 3;
 * the app-shell banner turns amber after a few seconds of `reconnecting`):
 *   `'connecting'` when a session's socket is dialled, `'open'` on every
 *   authenticated open, `'reconnecting'` on every unplanned close (a first
 *   attempt that never opened included — `connection.ts` retries either
 *   way), `'idle'` on a planned teardown (unmount / token change) and on a
 *   4002 supersede. `connection.ts` fires NO `onClose` for those last two,
 *   so this hook sets `'idle'` itself.
 */
export function useRealtime(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Hold a ref to the latest queryClient so the connection's lifetime
  // callbacks don't capture a stale closure. (In practice the QueryClient is
  // a singleton, but this keeps the hook portable for tests that swap it.)
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  // Same stale-closure concern for `navigate` — react-router can hand back a
  // new function identity across renders and the connection's callbacks are
  // captured once, inside the effect below.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!accessToken) {
      // No token — nothing to connect. The boundary won't even mount the
      // children in this state, but the early-return keeps the hook safe to
      // call before auth resolves.
      return;
    }

    // Open the connection. `disconnect` is the cleanup the React effect
    // returns; calling it sets the shutdown flag and closes the socket.
    connectivity.setWs('connecting');
    const disconnect = connect(accessToken, {
      onEvent: (event) => {
        // Lazy getter on the UI store — read the LATEST
        // selectedConversationId at the moment the event arrives. Capturing
        // the value at hook-mount time would mean every projected event uses
        // a stale "is the user viewing this conversation?" predicate.
        projectEvent(
          queryClientRef.current,
          event,
          () => useUIStore.getState().selectedConversationId,
          () => useUIStore.getState().selectedThread,
        );
      },
      onGap: () => {
        resetRealtimeFedCaches(queryClientRef.current);
        // A single quiet toast so the user knows the inbox refreshed.
        // Sonner dedupes the same string within ~3s, so a burst of gap
        // notices (rare) doesn't pile up.
        toast('Reconnected — refreshing your inbox');
      },
      // We deliberately do NOT toast on every reconnect-open; the dev-tools
      // network panel is enough signal during dev and the user shouldn't
      // see a connection toast every time a wifi roam happens. The socket
      // state is published to the connectivity store instead — the
      // app-shell banner is a selector over it (and waits out a grace
      // period, so a wifi roam stays silent there too).
      onOpen: () => {
        connectivity.setWs('open');
      },
      onClose: () => {
        // Only unplanned closes reach here; connection.ts reconnects.
        connectivity.setWs('reconnecting');
      },
      onSessionSuperseded: () => {
        // The Hub actively kicked this socket (close code 4002) because the
        // agent logged in on another device. `connection.ts` has already
        // stopped the reconnect loop by the time this fires. Mirror the
        // axios 401 path in `lib/api-client.ts` — silent session clear, drop
        // the ['auth'] query so RequireAuth doesn't refire /auth/me against
        // a token the Hub has already invalidated, ONE gentle toast (no
        // dialog, no console.error) — but land on a distinct destination so
        // LoginPage can render a distinct, calmer message than a generic
        // "session expired".
        //
        // No onClose follows a 4002 and nothing reconnects: the socket is
        // idle, not reconnecting.
        connectivity.setWs('idle');
        useAuthStore.getState().clear();
        void queryClientRef.current.cancelQueries({ queryKey: ['auth'] });
        queryClientRef.current.removeQueries({ queryKey: ['auth'] });
        toast('Signed out — your account signed in on another device');
        navigateRef.current('/login?reason=elsewhere', { replace: true });
      },
    });

    return () => {
      disconnect();
      // Planned teardown: connection.ts suppresses onClose for it.
      connectivity.setWs('idle');
    };
    // We deliberately do NOT depend on `queryClient` — its identity is
    // stable for the lifetime of the QueryClientProvider, and adding it to
    // the deps would re-open the socket on every render that happens to
    // bring a new QueryClient (none, in practice). The ref above handles
    // the edge case.
     
  }, [accessToken]);
}
